/**
 * useMenuPosition — keep a floating menu (right-click context menu, popover,
 * dropdown anchored to mouse coords) completely inside the viewport.
 *
 * The hook is given a ref to the menu's outer element and the menu state
 * object (`{x, y, ...} | null`). It returns:
 *
 *   { left, top, maxHeight, maxWidth, ready }
 *
 * - `left`/`top` — CSS strings, ready to pass to `style` or Chakra props.
 * - `maxHeight`/`maxWidth` — CSS strings, clamp the menu's box so an
 *   overlong menu becomes internally scrollable rather than spilling off
 *   the screen.
 * - `ready` — false on the first frame (before measurement) so callers
 *   can hide the menu with `visibility: hidden` until clamped coords are
 *   ready. This prevents the "flash at raw cursor position then jump"
 *   that the previous implementation suffered from when the menu was
 *   near a viewport edge.
 *
 * Why the rewrite
 * ---------------
 * The previous version had three latent bugs:
 *
 *   1. `ref.current` was listed as a useLayoutEffect dependency, but refs
 *      are not reactive — the effect did not re-run when the ref attached
 *      after mount. Result: on the very first menu open the measurement
 *      sometimes used stale dimensions.
 *
 *   2. State `pos` from a previous menu open was retained when the user
 *      right-clicked elsewhere. The new menu rendered at the OLD clamped
 *      coords for one frame, then jumped — visually distracting and on
 *      slow devices the user would click before the jump.
 *
 *   3. There was no `maxHeight` return, so a tall menu near the bottom of
 *      the screen could still extend off-screen after "clamping" because
 *      the clamp was Math.max(margin, vh - rect.height - margin) — when
 *      rect.height > vh that returns the margin and the bottom is still
 *      cut off.
 *
 * The new implementation rebuilds position on every menu state change,
 * uses requestAnimationFrame for the measurement so the DOM is fully
 * laid out, and ALWAYS returns a maxHeight bounded by the available
 * viewport space below the clamped top.
 */
import { useLayoutEffect, useState, useRef } from 'react';

export default function useMenuPosition(ref, menu, opts = {}) {
  const margin = opts.margin ?? 8;
  // Bottom clearance: at least 10% of the viewport height (PME rule) so a
  // menu opened low on the page draws UPWARD and never kisses the bottom
  // edge. Overridable via opts.bottomMarginPct.
  const bottomPct = opts.bottomMarginPct ?? 0.1;
  const [computed, setComputed] = useState({ ready: false });
  // Track the menu coords this state corresponds to. Comparing on each
  // measurement run lets us discard stale results from a previous menu open.
  const lastKeyRef = useRef('');

  // useLayoutEffect with explicit menu.x / menu.y in the deps so opening a
  // different context menu (different coords) re-triggers measurement
  // immediately.
  useLayoutEffect(() => {
    if (!menu) {
      lastKeyRef.current = '';
      setComputed({ ready: false });
      return;
    }
    const key = `${menu.x},${menu.y}`;
    lastKeyRef.current = key;

    const measure = () => {
      if (lastKeyRef.current !== key) return; // a newer menu has opened
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const bottomMargin = Math.max(margin, Math.round(vh * bottomPct));

      // Available space below / right of the cursor. We prefer to open
      // down+right (the natural direction), but if the menu doesn't fit
      // we flip up / left.
      const spaceBelow = vh - menu.y - bottomMargin;
      const spaceAbove = menu.y - margin;
      const spaceRight = vw - menu.x - margin;
      const spaceLeft  = menu.x - margin;

      // Pick the direction with more room, but only flip if down/right
      // is genuinely too small for the menu.
      let top;
      if (rect.height + margin <= spaceBelow) {
        top = menu.y;
      } else if (rect.height + margin <= spaceAbove) {
        top = menu.y - rect.height; // flip above the cursor
      } else {
        // Neither side fits — clamp to whichever side has more room and
        // let maxHeight handle the rest via internal scroll.
        top = spaceBelow >= spaceAbove ? menu.y : margin;
      }

      let left;
      if (rect.width + margin <= spaceRight) {
        left = menu.x;
      } else if (rect.width + margin <= spaceLeft) {
        left = menu.x - rect.width;
      } else {
        left = spaceRight >= spaceLeft ? menu.x : margin;
      }

      // Final hard clamp so the box never overflows even in the
      // "neither side fits" case.
      if (left + rect.width > vw - margin) left = Math.max(margin, vw - rect.width - margin);
      if (top  + rect.height > vh - bottomMargin) top  = Math.max(margin, vh - rect.height - bottomMargin);
      if (left < margin) left = margin;
      if (top  < margin) top  = margin;

      // maxHeight/maxWidth so an oversized menu becomes internally
      // scrollable instead of leaking past the viewport.
      const maxHeight = Math.max(60, vh - top - bottomMargin);
      const maxWidth  = Math.max(120, vw - left - margin);

      setComputed({
        ready: true,
        left:      `${left}px`,
        top:       `${top}px`,
        maxHeight: `${maxHeight}px`,
        maxWidth:  `${maxWidth}px`,
      });
    };

    // Synchronous measurement (catches the simple case)
    measure();
    // Re-measure after the browser has had a chance to paint, in case
    // the menu's contents grew after the initial layout (lazy icons,
    // streamed children, etc.).
    const raf = requestAnimationFrame(measure);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && ref.current) {
      ro = new ResizeObserver(() => measure());
      ro.observe(ref.current);
    }
    return () => { cancelAnimationFrame(raf); if (ro) ro.disconnect(); };
    // eslint-disable-next-line
  }, [menu, menu?.x, menu?.y]);

  if (!menu) return { ready: false, left: '0px', top: '0px', maxHeight: 'none', maxWidth: 'none' };
  return computed.ready
    ? computed
    // First-render fallback: render at raw coords but hidden so the user
    // never sees the unclamped position. The component should pair this
    // with visibility:hidden when ready === false.
    : { ready: false, left: `${menu.x}px`, top: `${menu.y}px`, maxHeight: 'none', maxWidth: 'none' };
}
